import { ComponentPropsWithoutRef } from 'react';
import Markdown from 'shared/components/Markdown';
import { FONT_SIZES, LINE_HEIGHTS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { BASE } from 'design-system/variables/spacing';
import { materialFieldSSTStyles, materialLabelStyles } from 'inquiry/mixins/form';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputTextComponent } from 'lib/interfaces/ui-components';

interface InputProps extends FormControlProps {
  styles: InputTextComponent['styles'] | undefined;
}

// 40px
export const MATERIAL_INPUT_HEIGHT = parseInt(FONT_SIZES.input) * LINE_HEIGHTS.input + BASE * 2;

const Input = styled('input')<
  InputProps & {
    focused: boolean | undefined;
    hasError: boolean | undefined;
    hasWarning: boolean | undefined;
  }
>`
  ${materialFieldSSTStyles}
`;

const StyledMarkdown = styled(Markdown)<
  InputProps & {
    focused: boolean | undefined;
    hasError: boolean | undefined;
    hasValue: boolean | undefined;
    hasWarning: boolean | undefined;
  }
>`
  ${materialLabelStyles}

  ${({ hasError, hasValue }) =>
    (hasError || hasValue) &&
    css`
      top: -14px;
      font-size: ${FONT_SIZES.small};
    `};
`;

const Container = styled('div')`
  position: relative;
`;

interface MaterialInputProps {
  labelText: string;
  value?: string;
  focused?: boolean;
  styles?: InputTextComponent['styles'];
}

export default function MaterialInput({
  className,
  focused,
  hasError,
  hasWarning,
  labelText,
  styles,
  ...passthroughProps
}: MaterialInputProps & ComponentPropsWithoutRef<'input'> & FormControlProps) {
  return (
    <Container className={className}>
      <Input
        focused={focused}
        hasError={hasError}
        hasWarning={hasWarning}
        // Avoid spell-jacking
        spellCheck={false}
        styles={styles}
        {...passthroughProps}
      />
      <StyledMarkdown
        wrapper='label'
        wrapperProps={{ htmlFor: passthroughProps.id }}
        focused={focused}
        hasError={hasError}
        hasValue={Boolean(passthroughProps.value)}
        hasWarning={hasWarning}
        styles={styles}
      >
        {labelText}
      </StyledMarkdown>
    </Container>
  );
}
